import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Spin } from 'antd';
import moment from 'moment';
import { emitToastrWarning, handleFetchError, handleFetchSuccess, getBulkFillDateBoundsFromSelectedDays } from '@/helpers';
import { engineeringEntryActions, projectEntryActions, bulkEntryActions, labEntryActions, timeBucketActions } from '@/store';
import { SupportedSystems, HoursLimit } from '@/shared';
import { errorSavingEntry, errorSavingEntryHours } from '@/shared/constants/toastrErrorMsg';
import { FillWeekWrapper, FillWeekInput, FillWeekBtn } from './styled';

function getErrMsg(e) {
  if (!e) return errorSavingEntry;
  if (e.Message) return { title: 'Error: Project Entries', message: e.Message };
  if (e.message === errorSavingEntryHours.message) return errorSavingEntryHours;
  if (e.message) return { title: 'Error: Project Entries', message: e.message };
  return errorSavingEntry;
}

function resolveDateRange(type, projectData, selectedDays, monthsAllowed) {
  switch (type) {
    case SupportedSystems.VPIN: {
      const { canEditByDay } = projectData;
      if (!canEditByDay || typeof canEditByDay !== 'object') return null;
      // Use canEditByDay only — not entryData Map keys. The row's Map is rebuilt only when
      // project.entries changes; canEditByDay can change for the new week with the same [] ref,
      // leaving stale keys and making every canEditByDay[d] lookup fail.
      const editableDates = Object.keys(canEditByDay)
        .filter((d) => canEditByDay[d])
        .sort((a, b) => moment(a).valueOf() - moment(b).valueOf());
      if (editableDates.length === 0) return null;
      return {
        startDate: moment(editableDates[0]).format('YYYY-MM-DD'),
        endDate: moment(editableDates[editableDates.length - 1]).format('YYYY-MM-DD'),
      };
    }
    case SupportedSystems.LRS:
    case SupportedSystems.ADMIN:
      return getBulkFillDateBoundsFromSelectedDays(selectedDays, monthsAllowed);
    default:
      return null;
  }
}

function buildBody(type, { startDate, endDate, userId, hours, projectData }) {
  const common = { startDate, endDate, userId, hours, timezone: new Date().getTimezoneOffset(), excludeWeekends: true };

  switch (type) {
    case SupportedSystems.VPIN:
      return { ...common, projectId: projectData.projectId, projectNumber: projectData.projectNumber };
    case SupportedSystems.LRS: {
      const { procedure } = projectData;
      const procId = procedure.procedureId ?? procedure.timeEntries?.[0]?.timeEntry?.procedureId ?? procedure.timeEntries?.[0]?.procedureId;
      return {
        ...common,
        requestNumber: projectData.requestNumber,
        requestId: projectData.requestId,
        projectNumber: projectData.projectNumber,
        procedureId: procId,
        isNewLR: projectData.isNewLR,
        procedure: {
          Id: procedure.id,
          ProcedureIdLegacy: procId,
          ProcedureNumber: procedure.procedureNumber,
          ProcedureAlphaId: procedure.procedureAlphaId,
          ProcedureTitle: procedure.procedureTitle,
          ProcedureExplanation: procedure.procedureExplanation,
        },
      };
    }
    case SupportedSystems.ADMIN:
      return { ...common, TimeBucketId: projectData.timeBucketId };
    default:
      throw new Error(`Unknown type: ${type}`);
  }
}

async function refreshAfterBulk(type, dispatch, userId, refreshStart, refreshEnd, selectedDays, projectData, onSuccess) {
  switch (type) {
    case SupportedSystems.VPIN:
      await dispatch(engineeringEntryActions.getEngineeringProjectTimeEntries(userId, refreshStart, refreshEnd, { skipPageLoader: true }));
      break;
    case SupportedSystems.LRS:
      await dispatch(labEntryActions.getLabTimeEntries(userId, refreshStart, refreshEnd, { skipPageLoader: true }));
      break;
    case SupportedSystems.ADMIN: {
      const data = await dispatch(timeBucketActions.getTimeBucketEntriesForCategory(userId, selectedDays, projectData.categoryId));
      if (onSuccess) onSuccess(data);
      break;
    }
    default:
      break;
  }
}

const FillWeekControl = ({ type, projectData, onSuccess, disabled }) => {
  const dispatch = useDispatch();
  const userId = useSelector(state => state.user.activeUser?.oid);
  const selectedDays = useSelector(state => state.projectEntry.projectEntries.selectedDays);
  const monthsAllowed = useSelector(state => state.config.configuration.NumberOfPreviousTimeEntryMonthsAllowed);

  const [hoursInput, setHoursInput] = useState(null);
  const [loading, setLoading] = useState(false);

  const validateAndRound = (value) => {
    if (value === null || value === undefined || value === '') {
      return 0;
    }

    const numValue = typeof value === 'number' ? value : Number(value);

    if (isNaN(numValue) || numValue < 0) {
      emitToastrWarning('Hours', `Please enter a valid positive number up to ${HoursLimit} hours per day.`);
      return null;
    }

    if (numValue === 0) {
      return 0;
    }

    if (numValue > HoursLimit) {
      emitToastrWarning('Hours', `${numValue} exceeds the maximum of ${HoursLimit} hours per day.`);
      return null;
    }

    // Same half-hour rounding as Bulk Time modal.
    // Any non-zero fraction becomes .5 on that hour (e.g. 8.2 → 8.5).
    const strValue = numValue.toString();
    if (strValue.indexOf('.') > -1 && Number(strValue.split('.')[1]) > 0) {
      return Math.min(Number(`${strValue.split('.')[0]}.5`), HoursLimit);
    }

    return numValue;
  };

  const handleClick = async () => {
    const validHours = validateAndRound(hoursInput);
    if (validHours === null) return;

    const range = resolveDateRange(type, projectData, selectedDays, monthsAllowed);
    if (!range) {
      emitToastrWarning('Fill Week', 'No editable days in this week.');
      return;
    }
    const { startDate, endDate } = range;
    const refreshStart = moment(selectedDays[0]).format('YYYY-MM-DD');
    const refreshEnd = moment(selectedDays[6]).format('YYYY-MM-DD');

    const body = buildBody(type, { startDate, endDate, userId, hours: validHours, projectData });

    setLoading(true);
    try {
      const result = await dispatch(bulkEntryActions.addBulkTimeEntry(body, type));
      if (!Array.isArray(result) || result.length === 0) {
        emitToastrWarning('Fill Week', 'No editable days in this week.');
        return;
      }
      await refreshAfterBulk(type, dispatch, userId, refreshStart, refreshEnd, selectedDays, projectData, onSuccess);
      dispatch(projectEntryActions.requestMonthlyCompletion(userId, moment(selectedDays[0]).format(), 0));
      handleFetchSuccess('Fill Week');
      setHoursInput(null);
    } catch (e) {
      handleFetchError(e, getErrMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      setHoursInput(null);
      return;
    }
    const n = Number(raw);
    if (!Number.isNaN(n)) {
      setHoursInput(n);
    }
  };

  if (loading) {
    return (
      <FillWeekWrapper>
        <Spin size="small" />
      </FillWeekWrapper>
    );
  }

  return (
    <FillWeekWrapper>
      <FillWeekInput
        type="number"
        min={0}
        max={HoursLimit}
        step={0.5}
        value={hoursInput === null ? '' : hoursInput}
        onChange={handleInputChange}
        onFocus={(e) => e.target.select()}
        disabled={disabled}
        placeholder={0}
      />
      <FillWeekBtn
        type="button"
        onClick={handleClick}
        disabled={disabled}
      >
        Fill Week
      </FillWeekBtn>
    </FillWeekWrapper>
  );
};

FillWeekControl.propTypes = {
  type: PropTypes.string.isRequired,
  projectData: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
  disabled: PropTypes.bool,
};

FillWeekControl.defaultProps = {
  disabled: false,
  onSuccess: null,
};

export default FillWeekControl;
